import { useEffect, useMemo, useRef, useState } from "react";
import type { RepairedRungDto } from "@/api/types";
import { addMonths, fmtByDate, fmtDate, fmtMonth, idx, monthAt, N, sortMonths } from "@/lib/months";
import { pct } from "@/lib/format";
import { Switch } from "@/components/ui/switch";
import { useStore } from "@/model/store";
import type { MarketLadder } from "@/model/types";
import { cn } from "@/lib/utils";
import { linePath, niceTicks, PAD, useChartSurface, useWidth, yearTicks, type Zoom } from "./chart-utils";
import { ChartLegend, type LegendItem } from "./ChartToolbar";

export type { Zoom } from "./chart-utils";

type By = Record<string, number>;

interface Props {
	by: By;
	/** The curve through `by` to draw: the API's exact series, or a straight-line preview while dragging. */
	statedF: ArrayLike<number>;
	onChange?: (draft: By) => void;
	onCommit?: (by: By) => void;
	/** "When it happens": the chance per period in view, saved as `{in, zoom}`; the API keeps each period's shape. */
	onCommitPeriods?: (periods: Record<string, number>, zoom: Zoom) => Promise<unknown> | void;
	implied?: ArrayLike<number> | null;
	scenario?: ArrayLike<number> | null;
	scenarioLabel?: string;
	/** Colour of the editable curve: the baseline, or a scenario's own values. */
	tone?: "baseline" | "scenario";
	statedLabel?: string;
	/** A grey reference line (e.g. Baseline while editing a scenario). */
	ghost?: ArrayLike<number> | null;
	ghostLabel?: string;
	readOnly?: boolean;
	height?: number;
	viewEnd: number;
	mode?: "curve" | "bars";
	/** Period size for the "When it happens" bars. */
	zoom?: Zoom;
	/** When dragging a bar, take the chance from the other bars instead of from "Not by…". */
	keepTotal?: boolean;
	market?: MarketLadder | null;
	/** The market's rungs, repaired to never decrease (from the API), at their grid positions. */
	rungs?: RepairedRungDto[] | null;
	/** Market chance by the end of each grid month it covers (null after its last rung). */
	marketMonthly?: (number | null)[] | null;
	happened?: string | null;
	/** Hide the stated curve (calculated events have none). */
	stated?: boolean;
	/** Draw the legend under the chart, built from the series shown. */
	legend?: boolean;
}

const clampP = (p: number) => Math.min(0.99, Math.max(0, p));

/** Keep the curve rising: values before `month` ≤ v, after ≥ v. */
function pushNeighbours(by: By, month: string, v: number): By {
	const out: By = {};
	for (const m of Object.keys(by)) {
		if (m === month) continue;
		const cmp = idx(m) - idx(month);
		out[m] = cmp < 0 ? Math.min(by[m], v) : Math.max(by[m], v);
	}
	out[month] = v;
	return out;
}

export interface Bucket {
	start: number; // first grid month
	end: number; // last grid month
	label: string;
}

const Q = ["Q1", "Q2", "Q3", "Q4"];

/** Periods from the first grid month to `t1` at the given zoom. */
export function buckets(zoom: Zoom, t1: number): Bucket[] {
	const out: Bucket[] = [];
	let start = 0;
	for (let t = 0; t <= t1; t++) {
		const m = monthAt(t);
		const mm = Number(m.slice(5));
		const isEnd = zoom === "month" || (zoom === "quarter" ? mm % 3 === 0 : mm === 12) || t === t1;
		if (!isEnd) continue;
		const y = m.slice(0, 4);
		const label = zoom === "month" ? fmtMonth(m) : zoom === "quarter" ? `${Q[Math.ceil(mm / 3) - 1]} ${y}` : y;
		out.push({ start, end: t, label });
		start = t + 1;
	}
	return out;
}

export function bucketMass(F: ArrayLike<number>, b: Bucket): number {
	return Math.max(0, F[b.end] - (b.start > 0 ? F[b.start - 1] : 0));
}

/** "Most likely: Nov 2026 (38%)" for the current zoom. */
export function mostLikely(F: ArrayLike<number>, bs: Bucket[]): { label: string; p: number } | null {
	let best: { label: string; p: number } | null = null;
	for (const b of bs) {
		const p = bucketMass(F, b);
		if (p > 0.004 && (!best || p > best.p)) best = { label: b.label, p };
	}
	return best;
}

/** The period key the API understands for a bucket ending in month `m`: "2027", "2027-Q3" or "2027-03". */
function periodKey(m: string, zoom: Zoom): string {
	if (zoom === "month") return m;
	if (zoom === "quarter") return `${m.slice(0, 4)}-Q${Math.ceil(Number(m.slice(5, 7)) / 3)}`;
	return m.slice(0, 4);
}

const r4 = (p: number) => Math.round(p * 10000) / 10000;

/**
 * Bar arithmetic for "When it happens" (plan P2 allows this in the browser): the dragged bar takes its
 * chance from "Not by…", or with `keepTotal` from the other bars in view, proportionally. Dragging
 * "Not by…" scales every bar in view. Returns the new bars and the periods that changed.
 */
function editMasses(m0: number[], after: number, target: number | "rest", v: number, keepTotal: boolean): { masses: number[]; changed: number[] } {
	const total = m0.reduce((a, b) => a + b, 0);
	const all = m0.map((_, i) => i);
	if (target === "rest") {
		// `v` is the new "Not by…" share: everything outside the view (after it, or never).
		const tot = Math.max(0, Math.min(0.99 - after, 1 - v));
		const f = total > 1e-9 ? tot / total : 0;
		return { masses: m0.map((m) => (total > 1e-9 ? m * f : tot / m0.length)), changed: all };
	}
	const old = m0[target];
	const maxV = keepTotal ? total : 0.99 - after - (total - old);
	const nv = Math.max(0, Math.min(maxV, v));
	const masses = m0.slice();
	masses[target] = nv;
	if (!keepTotal) return { masses, changed: [target] };
	const others = total - old;
	const want = total - nv;
	for (let i = 0; i < masses.length; i++) if (i !== target) masses[i] = others > 1e-9 ? (m0[i] * want) / others : want / Math.max(1, masses.length - 1);
	return { masses, changed: all };
}

type Drag =
	| { kind: "handle"; month: string; start: By; removing: boolean }
	| { kind: "body"; sx: number; sy: number; start: By; moved: boolean }
	| { kind: "bar"; target: number | "rest"; start: number[]; max: number };

/** Bar scale whose half is a whole percent: steps of 2 points up to 10%, then of 10 points. */
function niceBarMax(raw: number): number {
	if (raw <= 0.1) return Math.max(0.04, Math.ceil(raw * 50 - 1e-9) / 50);
	return Math.min(1, Math.ceil(raw * 10 - 1e-9) / 10);
}

/** Hit area for a handle or bar: at least 44 px (J1), however small it looks. */
const HIT = 22;

export function CurveEditor({
	by,
	statedF,
	onChange,
	onCommit,
	onCommitPeriods,
	implied,
	scenario,
	scenarioLabel = "Scenario",
	tone = "baseline",
	statedLabel = "Baseline",
	ghost,
	ghostLabel = "Baseline",
	readOnly: readOnlyProp,
	height = 300,
	viewEnd,
	mode = "curve",
	zoom = "year",
	keepTotal = false,
	market,
	rungs: rungsProp,
	marketMonthly,
	happened,
	stated = true,
	legend = false,
}: Props) {
	const surf = useChartSurface();
	const analyst = useStore((s) => s.analyst);
	// The node-page preview decides whether the chart is live (D1).
	const readOnly = surf.surface === "preview" ? !!surf.readOnly : !!readOnlyProp;
	const [wrapRef, W] = useWidth<HTMLDivElement>();
	const svgRef = useRef<SVGSVGElement>(null);
	const C = tone === "scenario" ? "var(--scenario)" : "var(--baseline)";
	const [drag, setDrag] = useState<Drag | null>(null);
	const [hover, setHover] = useState<{ t: number; py: number } | null>(null);
	const hoverT = hover?.t ?? null;
	const [focusMonth, setFocusMonth] = useState<string | null>(null);
	const [focusB, setFocusB] = useState<number | "rest" | null>(null);
	// "Zoom y" (editor only, curve view): scale to the data instead of 0–100% (D7).
	const [zoomY, setZoomY] = useState(false);
	const [frozenTop, setFrozenTop] = useState<number | null>(null);

	// Phones: a shorter preview (A2).
	const H = surf.surface === "preview" && W < 640 ? Math.min(height, 240) : height;
	const t0 = -1;
	const t1 = viewEnd;
	const REST = mode === "bars" ? 74 : 0;
	// The bars view labels its last bar on two lines.
	const padB = PAD.b + (mode === "bars" ? 14 : 0);
	const iw = W - PAD.l - PAD.r - REST;
	const ih = H - PAD.t - padB;

	const F = statedF;
	const canZoomY = surf.surface === "editor" && mode === "curve";
	const autoTop = useMemo(() => {
		let m = 0;
		const scan = (arr?: ArrayLike<number> | null) => {
			if (!arr) return;
			for (let t = 0; t <= Math.min(t1, N - 1); t++) m = Math.max(m, arr[t]);
		};
		if (stated) scan(F);
		scan(implied);
		scan(scenario);
		scan(ghost);
		if (marketMonthly) for (let t = 0; t <= Math.min(t1, N - 1); t++) m = Math.max(m, marketMonthly[t] ?? 0);
		return Math.min(1, Math.max(0.25, Math.ceil(m * 1.1 * 20 - 1e-9) / 20));
	}, [F, stated, implied, scenario, ghost, marketMonthly, t1]);
	const top = frozenTop ?? (canZoomY && zoomY ? autoTop : 1);

	const x = (t: number) => PAD.l + ((t - t0) / (t1 - t0)) * iw;
	const y = (p: number) => PAD.t + (1 - p / top) * ih;
	const tAt = (px: number) => Math.round(t0 + ((px - PAD.l) / iw) * (t1 - t0));
	const pAt = (py: number) => (1 - (py - PAD.t) / ih) * top;
	const fracAt = (py: number) => 1 - (py - PAD.t) / ih;
	const pxPerMonth = iw / (t1 - t0);

	const series = (arr: ArrayLike<number>) => {
		const pts: [number, number][] = [[x(-1), y(0)]];
		for (let t = 0; t <= Math.min(t1, N - 1); t++) pts.push([x(t), y(Math.min(arr[t], top * 1.02))]);
		return linePath(pts);
	};

	const points = sortMonths(Object.keys(by)).filter((m) => idx(m) >= 0 && idx(m) <= t1);

	const local = (e: React.PointerEvent) => {
		const r = svgRef.current!.getBoundingClientRect();
		return { px: e.clientX - r.left, py: e.clientY - r.top };
	};

	const change = (next: By) => onChange?.(next);

	function onDown(e: React.PointerEvent, d: Drag) {
		if (readOnly) return;
		e.stopPropagation();
		svgRef.current?.setPointerCapture(e.pointerId);
		setFrozenTop(top);
		setDrag(d);
	}

	function onMove(e: React.PointerEvent) {
		const { px, py } = local(e);
		if (!drag) {
			const t = tAt(px);
			setHover(px >= PAD.l && px <= W - PAD.r - REST && t >= 0 && t <= t1 ? { t, py } : null);
			return;
		}
		if (drag.kind === "handle") {
			const months = sortMonths(Object.keys(drag.start));
			const i = months.indexOf(drag.month);
			const lo = i > 0 ? idx(months[i - 1]) + 1 : 0;
			const hi = i < months.length - 1 ? idx(months[i + 1]) - 1 : N - 1;
			const t = Math.max(lo, Math.min(hi, tAt(px)));
			const m = monthAt(t);
			const removing = (py < PAD.t - 34 || py > H - padB + 30) && months.length > 1;
			const base = { ...drag.start };
			delete base[drag.month];
			const next = pushNeighbours(base, m, clampP(pAt(py)));
			if (m !== drag.month) setDrag({ ...drag, month: m, start: { ...base, [m]: drag.start[drag.month] }, removing });
			else if (removing !== drag.removing) setDrag({ ...drag, removing });
			change(removing ? base : next);
		} else if (drag.kind === "body") {
			const dx = Math.round((px - drag.sx) / pxPerMonth);
			const dy = py - drag.sy;
			if (!drag.moved && Math.abs(px - drag.sx) + Math.abs(dy) < 5) return;
			if (!drag.moved) setDrag({ ...drag, moved: true });
			const factor = Math.exp(-dy / 110);
			const next: By = {};
			for (const [m, v] of Object.entries(drag.start)) {
				const nm = addMonths(m, dx);
				if (idx(nm) < 0 || idx(nm) >= N) continue;
				next[nm] = clampP(v * factor);
			}
			if (Object.keys(next).length) change(next);
		} else if (drag.kind === "bar") {
			const f = Math.max(0, Math.min(1, fracAt(py)));
			const v = drag.target === "rest" ? f : f * drag.max;
			setBarDraft(editMasses(drag.start, afterView, drag.target, v, keepTotal));
		}
	}

	function endDrag(e: React.PointerEvent, cancelled: boolean) {
		if (!drag) return;
		const { px } = local(e);
		if (drag.kind === "body" && !drag.moved) {
			// Tap on the curve: add a point there.
			const t = Math.max(0, Math.min(t1, tAt(px)));
			const m = monthAt(t);
			if (!cancelled && !(m in by)) onCommit?.({ ...by, [m]: Math.round(F[t] * 100) / 100 });
		} else if (drag.kind === "bar") {
			if (!cancelled && barDraft) commitBars(barDraft);
			else setBarDraft(null);
		} else {
			const rounded: By = {};
			for (const [m, v] of Object.entries(by)) rounded[m] = Math.round(v * 1000) / 1000;
			onCommit?.(rounded);
		}
		setDrag(null);
		setFrozenTop(null);
	}

	function onKey(e: React.KeyboardEvent, month: string) {
		if (readOnly) return;
		const step = e.shiftKey ? 0.05 : 0.01;
		let next: By | null = null;
		let nm = month;
		if (e.key === "ArrowUp") next = pushNeighbours(by, month, clampP(by[month] + step));
		else if (e.key === "ArrowDown") next = pushNeighbours(by, month, clampP(by[month] - step));
		else if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
			const d = (e.key === "ArrowLeft" ? -1 : 1) * (e.shiftKey ? 5 : 1);
			nm = addMonths(month, d);
			const others = Object.keys(by).filter((m) => m !== month);
			if (idx(nm) < 0 || idx(nm) >= N || others.includes(nm)) return;
			const ms = sortMonths(Object.keys(by));
			const i = ms.indexOf(month);
			if ((i > 0 && idx(nm) <= idx(ms[i - 1])) || (i < ms.length - 1 && idx(nm) >= idx(ms[i + 1]))) return;
			next = { ...by };
			delete next[month];
			next[nm] = by[month];
		} else if ((e.key === "Delete" || e.key === "Backspace") && Object.keys(by).length > 1) {
			next = { ...by };
			delete next[month];
			nm = "";
		} else return;
		e.preventDefault();
		const rounded: By = {};
		for (const [m, v] of Object.entries(next)) rounded[m] = Math.round(v * 100) / 100;
		onCommit?.(rounded);
		setFocusMonth(nm || null);
	}

	// Moving a handle sideways re-keys it; keep keyboard focus on it.
	useEffect(() => {
		if (!focusMonth || !svgRef.current) return;
		const el = svgRef.current.querySelector<SVGElement>(`[data-month="${focusMonth}"]`);
		if (el && document.activeElement !== el) el.focus();
	}, [focusMonth, by]);

	// "When it happens" view: bars are differences of the curve at period ends (display arithmetic).
	const bs = useMemo(() => buckets(zoom, t1), [zoom, t1]);
	const primary: ArrayLike<number> | null = stated ? F : (implied ?? null);
	const [barDraft, setBarDraft] = useState<{ masses: number[]; changed: number[] } | null>(null);
	// A saved bar edit comes back as new points; the API's curve replaces the dragged bars.
	useEffect(() => setBarDraft(null), [by, zoom, t1]);
	const savedMasses = primary ? bs.map((b) => bucketMass(primary, b)) : bs.map(() => 0);
	const masses = barDraft?.masses ?? savedMasses;
	/** Chance after the view: it stays put when bars in view change. */
	const afterView = primary ? Math.max(0, primary[Math.min(N - 1, primary.length - 1)] - primary[Math.min(t1, primary.length - 1)]) : 0;

	function commitBars(d: { masses: number[]; changed: number[] }) {
		const periods: Record<string, number> = {};
		for (const i of d.changed) periods[periodKey(monthAt(bs[i].end), zoom)] = r4(d.masses[i]);
		setBarDraft(d);
		const done = onCommitPeriods?.(periods, zoom);
		if (!done) setBarDraft(null);
		else void Promise.resolve(done).finally(() => setBarDraft(null));
	}
	const impliedMasses = stated && implied ? bs.map((b) => bucketMass(implied, b)) : null;
	const marketMasses = marketMonthly
		? bs.map((b) => {
				const e = marketMonthly[b.end];
				const s0 = b.start > 0 ? marketMonthly[b.start - 1] : 0;
				return e === null || e === undefined || s0 === null || s0 === undefined ? null : Math.max(0, e - s0);
			})
		: null;
	const rest = barDraft ? Math.max(0, 1 - masses.reduce((a, b) => a + b, 0)) : primary ? 1 - primary[t1] : 1;
	const barMax =
		drag?.kind === "bar"
			? drag.max
			: // Scale to include the market, so its marks are never off the chart (A4).
				niceBarMax(Math.max(...masses, ...(impliedMasses ?? []), ...(marketMasses ?? []).map((m) => m ?? 0)) * 1.15);
	const yb = (p: number) => PAD.t + (1 - p / barMax) * ih;
	const restX = W - PAD.r - REST + 14;
	const restW = REST - 22;
	const restYear = monthAt(t1).slice(0, 4);
	// One label for the last bar, under the axis and in the readout (A18).
	const restLabel = t1 >= N - 1 ? `Not by ${restYear}` : `After ${restYear} or never`;
	const best = primary && !barDraft ? mostLikely(primary, bs) : null;
	const [hoverB, setHoverB] = useState<number | null>(null);

	function onBarKey(e: React.KeyboardEvent, target: number | "rest") {
		if (readOnly) return;
		const step = (e.shiftKey ? 0.05 : 0.01) * (e.key === "ArrowUp" ? 1 : e.key === "ArrowDown" ? -1 : 0);
		if (!step) return;
		e.preventDefault();
		const cur = target === "rest" ? rest : masses[target];
		commitBars(editMasses(masses, afterView, target, cur + step, keepTotal));
	}

	// Market ladder: repaired rungs (from the API), drawn at their exact dates; nothing after the last rung.
	const rungs = rungsProp ?? [];
	const marketEnd = rungs.length ? Math.max(...rungs.map((r) => r.pos)) : null;
	const maxVol = Math.max(1, ...rungs.map((r) => r.volume));

	const ticks = yearTicks(t0, t1, iw);
	const curveGrid = top >= 1 ? [0, 0.25, 0.5, 0.75, 1] : niceTicks(0, top, 4).filter((p) => p <= top + 1e-9);
	const hv = hoverT !== null ? { m: monthAt(hoverT), stated: F[hoverT], model: implied?.[hoverT], scen: scenario?.[hoverT] } : null;
	const happenedT = happened ? idx(happened) : null;
	const touch = { touchAction: "none" } as const;

	// Tooltip: follows the pointer vertically (D5); outside analyst mode, the two most relevant lines.
	const tipTop = (lines: number) => Math.max(0, Math.min(H - 30 - lines * 18, (hover?.py ?? 0) - 24));
	const curveLines: { k: string; el: React.ReactNode }[] = [];
	if (hv) {
		const t = hoverT!;
		if (stated)
			curveLines.push({
				k: "s",
				el: (
					<div style={{ color: C }}>
						{statedLabel} {pct(hv.stated)}
					</div>
				),
			});
		if (marketMonthly && marketMonthly[t] !== null && marketMonthly[t] !== undefined)
			curveLines.push({
				k: "mk",
				el: (
					<div className="text-[var(--market)]">
						{market?.venue ?? "Market"} {pct(marketMonthly[t])}
					</div>
				),
			});
		if (hv.model !== undefined)
			curveLines.push({
				k: "m",
				el: (
					<div className="text-[var(--model)]">
						{stated ? "Model" : "Calculated"} {pct(hv.model)}
					</div>
				),
			});
		if (hv.scen !== undefined)
			curveLines.push({
				k: "sc",
				el: (
					<div className="text-[var(--scenario-ink)]">
						{scenarioLabel}: {pct(hv.scen)}
					</div>
				),
			});
		if (ghost)
			curveLines.push({
				k: "g",
				el: (
					<div className="text-muted-foreground">
						{ghostLabel} {pct(ghost[t])}
					</div>
				),
			});
	}
	const shownCurveLines = analyst ? curveLines : curveLines.slice(0, 2);
	const barLines: { k: string; el: React.ReactNode }[] = [];
	if (mode === "bars" && hoverB !== null && bs[hoverB]) {
		barLines.push({
			k: "s",
			el: (
				<div style={{ color: stated ? C : "var(--model)" }}>
					{stated ? statedLabel : "Calculated"} {pct(masses[hoverB])}
				</div>
			),
		});
		if (marketMasses && marketMasses[hoverB] !== null)
			barLines.push({
				k: "mk",
				el: (
					<div className="text-[var(--market)]">
						{market?.venue ?? "Market"} {pct(marketMasses[hoverB])}
					</div>
				),
			});
		if (impliedMasses) barLines.push({ k: "m", el: <div className="text-[var(--model)]">Model {pct(impliedMasses[hoverB])}</div> });
	}
	const shownBarLines = analyst ? barLines : barLines.slice(0, 2);

	const legendItems: LegendItem[] = [
		...(stated ? [{ c: C, l: tone === "scenario" ? `${statedLabel}: values` : statedLabel }] : []),
		...(implied ? [{ c: "var(--model)", l: stated ? "Model" : "Calculated", dash: stated }] : []),
		...(scenario ? [{ c: "var(--scenario)", l: scenarioLabel }] : []),
		...(ghost ? [{ c: "var(--ink)", l: ghostLabel }] : []),
		...(market ? [{ c: "var(--market)", l: market.venue, dash: true }] : []),
	];

	const marketCaptionRight = marketEnd !== null && x(marketEnd) > PAD.l + iw * 0.55;

	return (
		<div ref={wrapRef} className="relative w-full select-none">
			<svg
				ref={svgRef}
				width={W}
				height={H}
				className={cn("block overflow-visible", drag && "cursor-grabbing")}
				onPointerMove={onMove}
				onPointerUp={(e) => endDrag(e, false)}
				onPointerCancel={(e) => endDrag(e, true)}
				onPointerLeave={() => setHover(null)}
				role="group"
				aria-label={mode === "curve" ? "Chance by date chart" : "When it happens chart"}
			>
				<defs>
					<pattern id="hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
						<rect width="6" height="6" fill="var(--grid)" />
						<line x1="0" y1="0" x2="0" y2="6" stroke="var(--ink)" strokeOpacity={0.35} strokeWidth={2} />
					</pattern>
				</defs>
				{/* Gridlines */}
				{(mode === "curve" ? curveGrid : [0, barMax / 2, barMax]).map((p) => (
					<g key={p} pointerEvents="none">
						<line x1={PAD.l} x2={W - PAD.r - REST} y1={mode === "curve" ? y(p) : yb(p)} y2={mode === "curve" ? y(p) : yb(p)} stroke="var(--grid)" />
						<text x={PAD.l - 8} y={(mode === "curve" ? y(p) : yb(p)) + 4} textAnchor="end" className="fill-muted-foreground text-[11px]">
							{pct(p)}
						</text>
					</g>
				))}
				{ticks.map((tk) => (
					<g key={tk.t} pointerEvents="none">
						<line x1={x(tk.t)} x2={x(tk.t)} y1={PAD.t} y2={H - padB} stroke="var(--grid)" strokeDasharray="2 3" />
						<text x={x(tk.t)} y={H - padB + 18} textAnchor="middle" className="fill-muted-foreground text-[11px]">
							{tk.label}
						</text>
					</g>
				))}
				<g pointerEvents="none">
					<line x1={x(-1)} x2={x(-1)} y1={PAD.t} y2={H - padB} stroke="var(--ink)" strokeOpacity={0.35} />
					<text x={x(-1) + 4} y={PAD.t + 10} className="fill-muted-foreground text-[11px]">
						now
					</text>
				</g>

				{happenedT !== null && happenedT <= t1 && (
					<g pointerEvents="none">
						<line x1={x(happenedT)} x2={x(happenedT)} y1={PAD.t} y2={H - padB} stroke="var(--gain)" strokeWidth={2} />
						<text x={x(happenedT) + 5} y={PAD.t + 24} className="fill-[var(--gain)] text-[11px] font-medium">
							Happened {fmtMonth(happened!)}
						</text>
					</g>
				)}

				{mode === "curve" ? (
					<>
						<g pointerEvents="none">
							{ghost && <path d={series(ghost)} fill="none" stroke="var(--ink)" strokeOpacity={0.3} strokeWidth={2} />}
							{implied && <path d={series(implied)} fill="none" stroke="var(--model)" strokeWidth={2} strokeDasharray="6 4" />}
							{scenario && <path d={series(scenario)} fill="none" stroke="var(--scenario)" strokeWidth={2.5} />}
							{stated && <path d={series(F) + `L${x(t1)},${y(0)}L${x(-1)},${y(0)}Z`} fill={C} fillOpacity={0.07} />}
							{stated && <path d={series(F)} fill="none" stroke={C} strokeWidth={3} strokeLinejoin="round" />}
						</g>
						{!readOnly && stated && (
							<path
								d={series(F)}
								fill="none"
								stroke="transparent"
								strokeWidth={22}
								className="cursor-grab"
								onPointerDown={(e) => {
									// Touch: only the points drag, so the page still scrolls over the line.
									if (e.pointerType === "touch") return;
									const { px, py } = local(e);
									onDown(e, { kind: "body", sx: px, sy: py, start: by, moved: false });
								}}
							>
								<title>Drag sideways for sooner or later, up or down for more or less likely. Click to add a point.</title>
							</path>
						)}
						{stated &&
							points.map((m) => {
								const t = idx(m);
								const removing = drag?.kind === "handle" && drag.month === m && drag.removing;
								const active = (drag?.kind === "handle" && drag.month === m) || focusMonth === m;
								const cy = y(Math.min(by[m], top * 1.02));
								return (
									<g key={m} transform={`translate(${x(t)},${cy})`}>
										{active && !removing && (
											<g pointerEvents="none">
												<rect x={-54} y={-38} width={108} height={24} rx={5} fill="var(--ink)" />
												<text y={-22} textAnchor="middle" className="fill-white text-xs font-medium">
													{pct(by[m])} by {fmtMonth(m)}
												</text>
											</g>
										)}
										{!readOnly && (
											<circle
												r={HIT}
												fill="transparent"
												className={drag ? undefined : "cursor-grab"}
												style={touch}
												onPointerDown={(e) => onDown(e, { kind: "handle", month: m, start: by, removing: false })}
											>
												<title>{`${pct(by[m])} by ${fmtMonth(m)}. Drag to change; drag off the chart to remove.`}</title>
											</circle>
										)}
										{/* Focus ring (D4): the handles are sliders you can reach with Tab. */}
										{focusMonth === m && !drag && <circle r={13} fill="none" stroke={C} strokeWidth={2} pointerEvents="none" />}
										<circle
											r={active ? 9 : 7}
											fill={removing ? "var(--risk)" : "#fff"}
											stroke={removing ? "var(--risk)" : C}
											strokeWidth={3}
											tabIndex={readOnly ? -1 : 0}
											role="slider"
											data-month={m}
											aria-label={`Chance by ${fmtByDate(m)}`}
											aria-valuemin={0}
											aria-valuemax={99}
											aria-valuenow={Math.round(by[m] * 100)}
											aria-valuetext={`${pct(by[m])} by ${fmtByDate(m)}`}
											className="pointer-events-none outline-none"
											onFocus={() => setFocusMonth(m)}
											onBlur={() => setFocusMonth((f) => (f === m ? null : f))}
											onKeyDown={(e) => onKey(e, m)}
										/>
									</g>
								);
							})}
						{drag?.kind === "handle" && drag.removing && (
							<text x={W / 2} y={PAD.t + 14} textAnchor="middle" className="fill-[var(--risk)] text-xs font-medium" pointerEvents="none">
								Release to remove this point
							</text>
						)}
					</>
				) : (
					<>
						{bs.map((b, i) => {
							const bx0 = x(b.start - 1);
							const bx1 = x(b.end);
							const gap = bx1 - bx0 > 8 ? 1.5 : 0.5;
							const bw = Math.max(1, bx1 - bx0 - gap * 2);
							const active = (drag?.kind === "bar" && drag.target === i) || hoverB === i;
							const showLabel = bw >= 26 || active;
							const barTop = yb(Math.min(barMax, masses[i]));
							const hitTop = Math.max(PAD.t - 8, barTop - 36);
							return (
								<g key={b.end} onPointerEnter={() => setHoverB(i)} onPointerLeave={() => setHoverB(null)}>
									<rect x={bx0} y={PAD.t} width={bx1 - bx0} height={ih} fill="transparent" />
									<rect
										x={bx0 + gap}
										y={barTop}
										width={bw}
										height={Math.max(1, H - padB - barTop)}
										rx={bw > 10 ? 3 : 1}
										fill={stated ? C : "var(--model)"}
										fillOpacity={active ? 0.85 : 0.55}
										className="pointer-events-none outline-none"
										tabIndex={readOnly ? -1 : 0}
										role="slider"
										aria-label={`Chance in ${b.label}`}
										aria-valuemin={0}
										aria-valuemax={100}
										aria-valuenow={Math.round(masses[i] * 100)}
										aria-valuetext={`${pct(masses[i])} in ${b.label}`}
										onKeyDown={(e) => onBarKey(e, i)}
										onFocus={() => {
											setHoverB(i);
											setFocusB(i);
										}}
										onBlur={() => {
											setHoverB(null);
											setFocusB(null);
										}}
									/>
									{focusB === i && (
										<rect
											x={bx0 + gap - 3}
											y={barTop - 3}
											width={bw + 6}
											height={Math.max(1, H - padB - barTop) + 6}
											rx={4}
											fill="none"
											stroke={C}
											strokeWidth={2}
											pointerEvents="none"
										/>
									)}
									{impliedMasses && (
										<rect
											x={bx0 + gap}
											y={yb(Math.min(barMax, impliedMasses[i]))}
											width={bw}
											height={Math.max(0, H - padB - yb(Math.min(barMax, impliedMasses[i])))}
											fill="none"
											stroke="var(--model)"
											strokeWidth={1.5}
											strokeDasharray="4 3"
											pointerEvents="none"
										/>
									)}
									{marketMasses && marketMasses[i] !== null && (
										<line
											x1={bx0 + gap}
											x2={bx0 + gap + bw}
											y1={yb(Math.min(barMax, marketMasses[i]!))}
											y2={yb(Math.min(barMax, marketMasses[i]!))}
											stroke="var(--market)"
											strokeWidth={3}
											strokeLinecap="round"
											pointerEvents="none"
										/>
									)}
									{showLabel && masses[i] >= 0.005 && (
										<text x={bx0 + gap + bw / 2} y={barTop - 6} textAnchor="middle" className="pointer-events-none fill-foreground text-[11px] font-medium">
											{pct(masses[i])}
										</text>
									)}
									{!readOnly && (
										<rect
											x={bx0}
											y={hitTop}
											width={bx1 - bx0}
											height={H - padB + 8 - hitTop}
											fill="transparent"
											className="cursor-ns-resize"
											style={touch}
											onPointerDown={(e) => onDown(e, { kind: "bar", target: i, start: masses, max: barMax })}
										/>
									)}
								</g>
							);
						})}
						{/* The last bar: its own 0–100% scale, so the other bars stay readable. */}
						<g>
							<line x1={restX - 8} x2={restX - 8} y1={PAD.t} y2={H - padB} stroke="var(--grid)" pointerEvents="none" />
							<rect x={restX} y={PAD.t} width={restW} height={ih} rx={4} fill="var(--grid)" fillOpacity={0.35} pointerEvents="none" />
							<rect
								x={restX}
								y={PAD.t + (1 - rest) * ih}
								width={restW}
								height={Math.max(1, rest * ih)}
								rx={4}
								fill="url(#hatch)"
								stroke="var(--ink)"
								strokeOpacity={0.35}
								className="pointer-events-none outline-none"
								tabIndex={readOnly ? -1 : 0}
								role="slider"
								aria-label={restLabel}
								aria-valuemin={0}
								aria-valuemax={100}
								aria-valuenow={Math.round(rest * 100)}
								aria-valuetext={`${pct(rest)} ${restLabel.toLowerCase()}`}
								onKeyDown={(e) => onBarKey(e, "rest")}
								onFocus={() => setFocusB("rest")}
								onBlur={() => setFocusB(null)}
							/>
							{focusB === "rest" && (
								<rect
									x={restX - 3}
									y={PAD.t + (1 - rest) * ih - 3}
									width={restW + 6}
									height={Math.max(1, rest * ih) + 6}
									rx={5}
									fill="none"
									stroke={C}
									strokeWidth={2}
									pointerEvents="none"
								/>
							)}
							<text
								x={restX + restW / 2}
								y={PAD.t + (1 - rest) * ih - 6}
								textAnchor="middle"
								className="pointer-events-none fill-foreground text-[11px] font-medium"
							>
								{pct(rest)}
							</text>
							<text x={restX + restW / 2} y={H - padB + 18} textAnchor="middle" className="fill-muted-foreground text-[11px]" pointerEvents="none">
								{t1 >= N - 1 ? (
									restLabel
								) : (
									<>
										<tspan x={restX + restW / 2}>After {restYear}</tspan>
										<tspan x={restX + restW / 2} dy={13}>
											or never
										</tspan>
									</>
								)}
							</text>
							{!readOnly && (
								<rect
									x={restX - 6}
									y={Math.max(PAD.t - 8, PAD.t + (1 - rest) * ih - 36)}
									width={restW + 12}
									height={H - padB + 8 - Math.max(PAD.t - 8, PAD.t + (1 - rest) * ih - 36)}
									fill="transparent"
									className="cursor-ns-resize"
									style={touch}
									onPointerDown={(e) => onDown(e, { kind: "bar", target: "rest", start: masses, max: barMax })}
								/>
							)}
						</g>
					</>
				)}

				{/* Market ladder (curve view): rungs at their exact dates, nothing after the last. */}
				{mode === "curve" && rungs.length > 0 && marketEnd !== null && (
					<g pointerEvents="none">
						<path
							d={linePath([
								[x(-1), y(0)],
								...rungs.filter((r) => r.pos >= -1 && r.pos <= t1).map((r) => [x(r.pos), y(Math.min(r.fair, top * 1.02))] as [number, number]),
							])}
							fill="none"
							stroke="var(--market)"
							strokeWidth={2}
							strokeDasharray="1 4"
							strokeLinecap="round"
						/>
						{rungs.map((r) =>
							r.pos < -1 || r.pos > t1 ? null : (
								<g key={r.date} transform={`translate(${x(r.pos)},${y(Math.min(r.fair, top * 1.02))})`}>
									<rect
										x={-(3 + 5 * Math.sqrt(r.volume / maxVol))}
										y={-(3 + 5 * Math.sqrt(r.volume / maxVol))}
										width={2 * (3 + 5 * Math.sqrt(r.volume / maxVol))}
										height={2 * (3 + 5 * Math.sqrt(r.volume / maxVol))}
										transform="rotate(45)"
										fill={r.resolved ? "#fff" : "var(--market)"}
										stroke="var(--market)"
										strokeWidth={1.5}
									/>
								</g>
							),
						)}
						{marketEnd <= t1 && (
							<g>
								<line x1={x(marketEnd)} x2={x(marketEnd)} y1={PAD.t} y2={H - padB} stroke="var(--market)" strokeOpacity={0.5} strokeDasharray="2 3" />
								{/* At the foot of its line, clear of "now" at the top (A5). */}
								<text
									x={x(marketEnd) + (marketCaptionRight ? -5 : 5)}
									y={H - padB - 6}
									textAnchor={marketCaptionRight ? "end" : "start"}
									className="fill-[var(--market)] text-[11px]"
									stroke="var(--card)"
									strokeWidth={3}
									paintOrder="stroke"
								>
									Market has no view after {fmtDate(rungs[rungs.length - 1].date)}
								</text>
							</g>
						)}
					</g>
				)}

				{hv && !drag && mode === "curve" && (
					<g pointerEvents="none">
						<line x1={x(hoverT!)} x2={x(hoverT!)} y1={PAD.t} y2={H - padB} stroke="var(--ink)" strokeOpacity={0.25} />
						{stated && <circle cx={x(hoverT!)} cy={y(Math.min(hv.stated, top * 1.02))} r={3.5} fill={C} />}
					</g>
				)}
			</svg>
			{hv && !drag && mode === "curve" && (
				<div
					className="pointer-events-none absolute z-10 rounded-md border bg-card px-2.5 py-1.5 text-xs shadow-sm"
					style={{ left: x(hoverT!) + 180 > W ? Math.max(0, x(hoverT!) - 180) : x(hoverT!) + 12, top: tipTop(shownCurveLines.length + 1) }}
				>
					<div className="font-medium">By {fmtMonth(hv.m)}</div>
					{shownCurveLines.map((l) => (
						<div key={l.k}>{l.el}</div>
					))}
				</div>
			)}
			{mode === "bars" && hoverB !== null && !drag && bs[hoverB] && (
				<div
					className="pointer-events-none absolute z-10 rounded-md border bg-card px-2.5 py-1.5 text-xs shadow-sm"
					style={{
						left: x(bs[hoverB].end) + 190 > W ? Math.max(0, x(bs[hoverB].start - 1) - 190) : x(bs[hoverB].end) + 8,
						top: hover ? tipTop(shownBarLines.length + 1) : 4,
					}}
				>
					<div className="font-medium">Chance in {bs[hoverB].label}</div>
					{shownBarLines.map((l) => (
						<div key={l.k}>{l.el}</div>
					))}
				</div>
			)}
			{mode === "bars" && (
				<div className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
					{best && (
						<span>
							Most likely {zoom}: <strong className="text-foreground">{best.label}</strong> ({pct(best.p)})
						</span>
					)}
					<span>
						{restLabel}: <strong className="text-foreground">{pct(rest)}</strong>
					</span>
				</div>
			)}
			{(legend || canZoomY || surf.readout) && (
				<div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
					{legend && <ChartLegend items={legendItems} />}
					{canZoomY && (
						<label
							className="ml-auto flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground"
							title="Scale the chart to the curves instead of 0–100%"
						>
							<Switch size="sm" checked={zoomY} onCheckedChange={setZoomY} />
							Zoom y
						</label>
					)}
					{surf.readout && <div className="flex w-full min-w-0 text-sm">{surf.readout}</div>}
				</div>
			)}
		</div>
	);
}
